import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationsCenterApi, NotificationItem, NotificationSummary } from '../services/notificationsCenterApi';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { useAuth } from './AuthContext';

interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  summary: NotificationSummary;
  loading: boolean;
  refresh: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAsUnread: (id: number) => Promise<void>;
  markAllAsRead: (category?: string) => Promise<void>;
  snooze: (id: number) => Promise<void>;
}

const EMPTY_SUMMARY: NotificationSummary = { total_unread: 0, per_category: {} };

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  summary: EMPTY_SUMMARY,
  loading: false,
  refresh: async () => {},
  markAsRead: async () => {},
  markAsUnread: async () => {},
  markAllAsRead: async () => {},
  snooze: async () => {},
});

// Window of recent notifications the Center groups client-side into
// Today/Yesterday/Earlier. Per-category unread counts come from the summary
// endpoint (accurate regardless of this window).
const LIST_LIMIT = 100;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [summary, setSummary] = useState<NotificationSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [items, sum] = await Promise.all([
        notificationsCenterApi.list({ limit: LIST_LIMIT }),
        notificationsCenterApi.summary(),
      ]);
      setNotifications(items || []);
      setSummary(sum || EMPTY_SUMMARY);
    } catch { /* ignore — the WS nudge / next poll retries */ }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      setSummary(EMPTY_SUMMARY);
      return;
    }
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // Polling is the safety net behind the realtime WS nudge; relaxed to 60s.
    intervalRef.current = setInterval(refresh, 60000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [user?.id, refresh]);

  // Realtime: refetch the moment a notification is created for this user.
  useNotificationSocket(refresh);

  const markAsRead = useCallback(async (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    setSummary((prev) => recomputeOnRead(prev, notifications, id, true));
    try { await notificationsCenterApi.markRead(id); } finally { void refresh(); }
  }, [notifications, refresh]);

  const markAsUnread = useCallback(async (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 0, snoozed_until: null } : n)));
    setSummary((prev) => recomputeOnRead(prev, notifications, id, false));
    try { await notificationsCenterApi.markUnread(id); } finally { void refresh(); }
  }, [notifications, refresh]);

  const markAllAsRead = useCallback(async (category?: string) => {
    setNotifications((prev) => prev.map((n) => (
      !category || category === 'all' || n.category === category ? { ...n, is_read: 1 } : n
    )));
    try { await notificationsCenterApi.markAllRead(category); } finally { void refresh(); }
  }, [refresh]);

  const snooze = useCallback(async (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    setSummary((prev) => recomputeOnRead(prev, notifications, id, true));
    try { await notificationsCenterApi.snooze(id); } finally { void refresh(); }
  }, [notifications, refresh]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount: summary.total_unread,
      summary,
      loading,
      refresh,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      snooze,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

/** Optimistically nudge the per-category + total counts when a single row's
 *  read state flips, so the badge/pills react instantly before refresh lands. */
function recomputeOnRead(
  summary: NotificationSummary,
  items: NotificationItem[],
  id: number,
  becomingRead: boolean,
): NotificationSummary {
  const item = items.find((n) => n.id === id);
  if (!item) return summary;
  const wasUnread = !item.is_read;
  // becoming read: decrement only if it was unread. becoming unread: increment
  // only if it was read. Same-state transitions don't move the counts.
  const delta = becomingRead ? (wasUnread ? -1 : 0) : (wasUnread ? 0 : 1);
  if (delta === 0) return summary;
  const cat = item.category || 'general';
  const per = { ...summary.per_category, [cat]: Math.max(0, (summary.per_category[cat] || 0) + delta) };
  return { total_unread: Math.max(0, summary.total_unread + delta), per_category: per };
}

export function useNotifications() {
  return useContext(NotificationContext);
}
